/* eslint-disable no-constant-condition */
import { MULTICALL_ABI } from 'constants/abi';
import { Contract, formatUnits, MaxUint256, parseUnits } from "ethers";
import { useCallback, useEffect, useState } from "react";
import { useNetworksStore } from 'store/networksStore';
import { attemptAggregate } from "utils/attemptAggregate";
import { getAbisByChainId } from 'utils/getAbisByChainId';
import { getEthersProvider, getEthersSigner } from "utils/wagmi.utils";
import { useAccount } from "wagmi";

export const useFactoryContract = (jsonRpcProvider) => {
  const { address: account } = useAccount();
	const { currentNetwork } = useNetworksStore();
  const [poolCreationFeeValue, setPoolCreationFeeValue] = useState("");
  const [xbrFeeValue, setXbrFeeValue] = useState(0);
  const [xStakingPools, setXStakingPools] = useState([]);
  const [factoryOwner, setFactoryOwner] = useState("");
  const [profitSharingFeeDenominator, setProfitSharingFeeDenominator] =
    useState(0);
		const [isLoadingState, setIsLoadingState] = useState(false)
		const currencyDecimals = currentNetwork.depositTokens[0].decimals

  const feeGettingMethods = [
		'getCurrentFees',
    "poolCreationFee",
    "getXStakingPools",
    "owner",
  ];

	if (currentNetwork?.xbrStakingPoolContractAddress) {
    feeGettingMethods.push('getXBRDiscount');
  }

  const getFactoryState = useCallback(async () => {
		if (!currentNetwork) return;
		setIsLoadingState(true)
    const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);

    const factoryJsonRpc = new Contract(
      currentNetwork?.factoryContractAddress,
      ABI_FACTORY,
      jsonRpcProvider
    );

    const multicallJsonRpc = new Contract(
      currentNetwork.multicallContractAddress,
      MULTICALL_ABI,
      jsonRpcProvider
    );

    if (!(jsonRpcProvider && factoryJsonRpc)) return;

    const calls = feeGettingMethods.map((method) => [
      currentNetwork?.factoryContractAddress,
      factoryJsonRpc.interface.encodeFunctionData(method),
    ]);

    try {
      const feesData = await attemptAggregate(multicallJsonRpc, calls);
      const decodedFeesData = feesData[1].map((aggregateItemResult, i) => {
        const data = factoryJsonRpc.interface.decodeFunctionResult(
          feeGettingMethods[i],
          aggregateItemResult
        );

        return data;
      });

      const [
				currentFees,
        poolCreationFeeArray,
        xstakingPoolsList,
        owner,
        xbrInfo,
      ] = decodedFeesData;

      const [poolCreationFee] = poolCreationFeeArray;
      const [list] = xstakingPoolsList;

      if (currentNetwork?.xbrStakingPoolContractAddress) {
        const [xbrFeeNumerator, xbrFeeDenominator] = xbrInfo;

        setXbrFeeValue(
          (parseFloat(xbrFeeNumerator.toString()) /
            parseFloat(xbrFeeDenominator.toString())) *
            100
        );
      } else {
				setXbrFeeValue(0)
			}

      setPoolCreationFeeValue(formatUnits(poolCreationFee, currencyDecimals));

      setProfitSharingFeeDenominator(100);

      setXStakingPools(list);

      setFactoryOwner(owner[0]);

			setIsLoadingState(false)

			console.log({stakingFees: currentFees?.[0], unstakingFees: currentFees?.[1]})

			return currentFees.map(feeType => [...feeType.map(fee => (
				{
					from: parseFloat(formatUnits(fee[0], currencyDecimals)).toString(),
					percentage: parseFloat(formatUnits(fee[2], 2)).toString(),
					fixedFee: parseFloat(formatUnits(fee[3], currencyDecimals)).toString(),
				}
			))])
    } catch (error) {
			console.error(error);
			setIsLoadingState(false)
    }
  }, [jsonRpcProvider]);


  const setStakingFeeNumerator = useCallback(
    async (value) => {
      if (!account) return;
			const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
      const signer = await getEthersSigner(currentNetwork.chainId);
      const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

      const tx = await factory.setStakingFeeNumerator(value);
      const _provider = await getEthersProvider();

      await _provider.waitForTransaction(tx.hash, 1);

      const receipt = await _provider.getTransactionReceipt(tx.hash);

      if (receipt.status === 0) {
        throw new Error("Transaction error", tx);
      }
    },
    [account, jsonRpcProvider]
  );

  const setUnstakingFeeNumerator = useCallback(
    async (value) => {
      if (!account) return;
			const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
      const signer = await getEthersSigner(currentNetwork.chainId);
      const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

      const tx = await factory.setUnstakingFeeNumerator(value);
      const _provider = await getEthersProvider();

      await _provider.waitForTransaction(tx.hash, 1);

      const receipt = await _provider.getTransactionReceipt(tx.hash);

      if (receipt.status === 0) {
        throw new Error("Transaction error", tx);
      }
    },
    [account, jsonRpcProvider]
  );

  const setXBRDiscountNumerator = useCallback(
    async (value) => {
      if (!account) return;
			const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
      const signer = await getEthersSigner(currentNetwork.chainId);
      const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

      const tx = await factory.setXBRDiscountNumerator(value);
      const _provider = await getEthersProvider();

      await _provider.waitForTransaction(tx.hash, 1);

      const receipt = await _provider.getTransactionReceipt(tx.hash);

      if (receipt.status === 0) {
        throw new Error("Transaction error", tx);
      }
    },
    [account, jsonRpcProvider]
  );

  const setPoolCreationFee = useCallback(
    async (value) => {
      if (!account) return;
			const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
      const signer = await getEthersSigner(currentNetwork.chainId);
      const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

      const tx = await factory.setPoolCreationFee(value);
      const _provider = await getEthersProvider();

      await _provider.waitForTransaction(tx.hash, 1);

      const receipt = await _provider.getTransactionReceipt(tx.hash);

      if (receipt.status === 0) {
        throw new Error("Transaction error", tx);
      }
    },
    [account, jsonRpcProvider]
  );

  const sweepToken = useCallback(
    async (address) => {
      if (!account) return;
			const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
      const signer = await getEthersSigner(currentNetwork.chainId);
      const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

      const tx = await factory.sweep(address);
      const _provider = await getEthersProvider();

      await _provider.waitForTransaction(tx.hash, 1);

      const receipt = await _provider.getTransactionReceipt(tx.hash);

      if (receipt.status === 0) {
        throw new Error("Transaction error", tx);
      }
    },
    [account, jsonRpcProvider]
  );

	const setFeesInfo = useCallback(async ({fees, isDeposit}) => {
		if (!account || !currentNetwork || !currencyDecimals) return;

		const { ABI_FACTORY } = getAbisByChainId(currentNetwork.chainId);
		const signer = await getEthersSigner(currentNetwork.chainId);
		const factory = new Contract(currentNetwork?.factoryContractAddress, ABI_FACTORY, signer);

		const parsedFees = fees.map((fee, i) => {
			return [
				i === 0 ? 1n : parseUnits(fee.from || '0', currencyDecimals),
				(i === fees?.length - 1) 
					? MaxUint256 
					: parseUnits(fees[i + 1].from || '0', currencyDecimals) - 1n,
				parseUnits(fee.percentage || '0', 2),
				parseUnits(fee.fixedFee || '0', currencyDecimals),
			]
		})

		const tx = await factory.setFeesInfo(parsedFees, isDeposit);
		const _provider = await getEthersProvider();

		await _provider.waitForTransaction(tx.hash, 1);

		const receipt = await _provider.getTransactionReceipt(tx.hash);

		if (receipt.status === 0) {
			throw new Error("Transaction error", tx);
		}
	}, [account, jsonRpcProvider])

  useEffect(() => {
    getFactoryState();
  }, [account, jsonRpcProvider]);

  return {
    profitSharingFeeDenominator,
    xbrFeeValue,
    getFactoryState,
    poolCreationFeeValue,
    xStakingPools,
    factoryOwner,
    setStakingFeeNumerator,
    setUnstakingFeeNumerator,
    setXBRDiscountNumerator,
    setPoolCreationFee,
    sweepToken,
		isLoadingState,
		setFeesInfo,
  };
};
